import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Check, Snowflake } from "lucide-react";
import DigestPreview from "@/components/DigestPreview";
import BrandModeration from "@/components/BrandModeration";

export default function Moderation() {
  const { user, loading } = useAuth();
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [frozen, setFrozen] = useState([]);
  const [presetFilter, setPresetFilter] = useState("");
  const [counts, setCounts] = useState(null);

  const fetchReports = async (preset = "") => {
    setLoadingReports(true);
    try {
      const { data } = await api.get("/moderation/reports", {
        params: preset ? { preset } : {},
      });
      setReports(data);
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not load reports.");
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const { data } = await api.get("/moderation/reports/counts");
      setCounts(data);
    } catch {}
  };

  const fetchFrozen = async () => {
    try {
      const { data } = await api.get("/moderation/frozen-branches");
      setFrozen(data);
    } catch {}
  };

  useEffect(() => {
    if (user?.is_owner) {
      fetchReports("");
      fetchCounts();
      fetchFrozen();
    }
  }, [user?.is_owner]);

  const changePreset = (p) => {
    setPresetFilter(p);
    fetchReports(p);
  };

  if (loading) return <div className="max-w-6xl mx-auto px-6 pt-20 text-[#2A2823]/60">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_owner) return <Navigate to="/grove" replace />;

  const dismiss = async (id) => {
    try {
      await api.post(`/moderation/reports/${id}/dismiss`);
      setReports((prev) => prev.filter((r) => r.id !== id));
      toast("Report dismissed — the Field Note stays.");
    } catch {
      toast("Could not dismiss.");
    }
  };

  const removeNote = async (report) => {
    const reason = window.prompt(
      "Confirm: describe the abuse in this Field Note.\n\nOnly remove if the note is abusive, harassing, or a personal attack. Never remove a merely-negative review.",
      report.reason
    );
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/field-notes/${report.note_id}`, { params: { reason } });
      setReports((prev) => prev.filter((r) => r.note_id !== report.note_id));
      toast("Field Note removed and report resolved.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not remove.");
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-24">
      <span className="accent-label text-[#B79256]">owner tools</span>
      <h1 className="font-serif-display text-5xl text-[#2A2823] mt-3 leading-none">Moderation</h1>

      <div className="mt-5 rounded-2xl border border-[#B79256]/30 bg-white/70 p-5 text-sm text-[#2A2823]/75 leading-relaxed">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-[#B79256] flex-shrink-0 mt-0.5" strokeWidth={1.75}/>
          <div>
            Only remove Field Notes that are <b>abusive, harassing, or a personal attack</b>.
            A Field Note that says "this broke after 6 months, wouldn't buy again" is a real review and must stay — even if it's negative or unflattering.
          </div>
        </div>
      </div>

      {frozen.length > 0 && (
        <section data-testid="frozen-digest" className="mt-8 rounded-2xl border border-[#7A5A2E]/40 bg-[#7A5A2E]/8 p-5">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Snowflake className="w-4 h-4 text-[#7A5A2E]" strokeWidth={2} />
              <h2 className="font-serif-display text-2xl text-[#2A2823]">Currently frozen</h2>
            </div>
            <span data-testid="frozen-count" className="accent-label text-[#7A5A2E]">
              {frozen.length} trusted {frozen.length === 1 ? "branch" : "branches"} paused
            </span>
          </div>
          <p className="text-xs text-[#2A2823]/60 mt-2">
            These branches auto-paused on the first flag. Open each one, decide, then either dismiss the report (thaws it) or remove the branch.
          </p>
          <ul className="mt-4 space-y-2">
            {frozen.map((b) => (
              <li key={b.id} data-testid={`frozen-row-${b.id}`}>
                <Link
                  to={`/idea/${b.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl bg-white/70 border border-[#7A5A2E]/25 px-4 py-3 hover:bg-white transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-serif-display text-lg text-[#2A2823] truncate">{b.title}</span>
                      {b.over_a_week && (
                        <span
                          data-testid={`frozen-over-week-${b.id}`}
                          className="accent-label text-[10px] rounded-full bg-[#B24A2A] text-[#FBF8F1] px-2 py-0.5"
                          title="Paused more than 7 days — please review"
                        >
                          over a week
                        </span>
                      )}
                    </div>
                    <div className="accent-label text-[#2A2823]/50 mt-0.5">
                      {b.provider_type || b.category}
                      {b.service_area ? ` · ${b.service_area}` : ""}
                      {b.days_frozen !== null && b.days_frozen !== undefined
                        ? ` · paused ${b.days_frozen} ${b.days_frozen === 1 ? "day" : "days"} ago`
                        : b.frozen_at ? ` · paused ${new Date(b.frozen_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  <span className="accent-label text-[#7A5A2E] whitespace-nowrap">
                    {b.unresolved_reports_count} open {b.unresolved_reports_count === 1 ? "flag" : "flags"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-10">
        {counts && (
          <div data-testid="report-preset-filter" className="mb-5 flex items-center gap-2 flex-wrap">
            <span className="accent-label text-[#B79256] mr-1">show:</span>
            {[
              { v: "", label: "all", n: counts.total },
              { v: "medical_claim", label: "medical", n: counts.medical_claim, tone: "text-[#B24A2A] border-[#B24A2A]/40" },
              { v: "personal_attack", label: "personal attack", n: counts.personal_attack, tone: "text-[#7A5A2E] border-[#7A5A2E]/40" },
              { v: "spam", label: "spam", n: counts.spam, tone: "text-[#4E5A3D] border-[#4E5A3D]/40" },
              { v: "other", label: "other", n: counts.other, tone: "text-[#8B6E3F] border-[#B79256]/40" },
            ].map((chip) => (
              <button
                key={chip.v || "all"}
                type="button"
                data-testid={`preset-chip-${chip.v || "all"}`}
                onClick={() => changePreset(chip.v)}
                className={`text-xs rounded-full px-3 py-1 border transition-colors ${
                  presetFilter === chip.v
                    ? "bg-[#2A2823] text-[#FBF8F1] border-[#2A2823]"
                    : `bg-white/60 hover:bg-white ${chip.tone || "text-[#2A2823]/70 border-[#B79256]/30"}`
                }`}
              >
                {chip.label} · {chip.n}
              </button>
            ))}
          </div>
        )}
        {loadingReports ? (
          <p className="text-[#2A2823]/50">Loading reports…</p>
        ) : reports.length === 0 ? (
          <div data-testid="mod-empty" className="text-center py-20 border border-dashed border-[#B79256]/40 rounded-3xl">
            <p className="font-serif-display text-3xl text-[#4E5A3D]">All quiet in The Grove.</p>
            <p className="text-[#2A2823]/60 mt-3">No open reports right now.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reports.map((r) => (
              <article key={r.id} data-testid={`report-${r.id}`} className="rounded-2xl border border-[#B79256]/30 bg-white p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="accent-label text-[#B79256]">reported by</div>
                    <p className="font-serif-display text-xl text-[#2A2823] mt-1">{r.reporter_name}</p>
                    <p className="accent-label text-[#2A2823]/50 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <Link to={`/idea/${r.idea_id}`} className="accent-label text-[#4E5A3D] hover:underline">on: {r.idea_title}</Link>
                </div>
                <div className="mt-4 border-l-2 border-[#B79256]/40 pl-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="accent-label text-[#B79256]">why they reported</span>
                    {r.reason_preset && (
                      <span
                        data-testid={`report-preset-badge-${r.id}`}
                        className={`accent-label text-[10px] rounded-full px-2 py-0.5 ${
                          r.reason_preset === "medical_claim" ? "bg-[#B24A2A]/15 text-[#B24A2A]" :
                          r.reason_preset === "personal_attack" ? "bg-[#7A5A2E]/15 text-[#7A5A2E]" :
                          r.reason_preset === "spam" ? "bg-[#4E5A3D]/15 text-[#4E5A3D]" :
                          "bg-[#B79256]/15 text-[#8B6E3F]"
                        }`}
                      >
                        {r.reason_preset === "medical_claim" ? "medical claim" :
                         r.reason_preset === "personal_attack" ? "personal attack" :
                         r.reason_preset === "spam" ? "spam" : "other"}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[#2A2823]/85">{r.details || r.reason}</p>
                </div>
                {r.note ? (
                  <div className="mt-4 rounded-xl bg-[#FBF8F1] border border-[#B79256]/25 p-4 text-sm">
                    <div className="accent-label text-[#B79256]">the field note ({r.note.author_name})</div>
                    <div className="mt-2 space-y-1 text-[#2A2823]/85">
                      <p><span className="accent-label text-[#B79256]">why:</span> {r.note.would_do_again_reason}</p>
                      <p><span className="accent-label text-[#B79256]">downside:</span> {r.note.downside}</p>
                      <p><span className="accent-label text-[#B79256]">not for:</span> {r.note.not_good_for}</p>
                    </div>
                  </div>
                ) : (
                  <p className="mt-4 text-[#2A2823]/50 italic">The note may have already been deleted.</p>
                )}
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    data-testid={`dismiss-${r.id}`}
                    onClick={() => dismiss(r.id)}
                    variant="outline"
                    className="rounded-full h-10 px-5 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]"
                  >
                    <Check className="w-4 h-4 mr-2" strokeWidth={1.75}/>Keep note (dismiss report)
                  </Button>
                  {r.note && (
                    <Button
                      data-testid={`remove-${r.id}`}
                      onClick={() => removeNote(r)}
                      className="rounded-full h-10 px-5 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
                    >
                      <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.75}/>Remove Field Note (abuse only)
                    </Button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <BrandModeration />
      <TrustedEmbedGenerator />
      <DigestPreview />
    </main>
  );
}

function TrustedEmbedGenerator() {
  const [area, setArea] = useState("");
  const [providerType, setProviderType] = useState("");
  const [limit, setLimit] = useState(4);
  const [analytics, setAnalytics] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://willoah.com";

  const fetchAnalytics = async () => {
    try {
      const { data } = await api.get("/moderation/embed-analytics");
      setAnalytics(data);
    } catch {}
  };

  useEffect(() => { fetchAnalytics(); }, []);
  const p = new URLSearchParams();
  if (area.trim()) p.set("area", area.trim());
  if (providerType.trim()) p.set("provider_type", providerType.trim());
  p.set("limit", String(limit));
  const src = `${origin}/embed/trusted?${p.toString()}`;
  const snippet = `<iframe src="${src}" width="100%" height="520" frameborder="0" style="border:0;max-width:640px;" title="Trusted providers on Willoah"></iframe>`;
  const areaLabel = area.trim() || "your neighborhood";
  const landingText =
    `Willoah invited neighbors near ${areaLabel} to share honest recommendations — the plumber who showed up when they said they would, the dentist who's patient with kids, the tutor who actually listens. Real people. No algorithm. See the list below and add your own on willoah.com.`;
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${label} copied.`);
    } catch {
      toast("Copy failed — try selecting the text manually.");
    }
  };
  return (
    <section data-testid="trusted-embed-generator" className="mt-12 border-t border-[#B79256]/25 pt-8">
      <h2 className="font-serif-display text-3xl text-[#2A2823]">Trusted directory widget</h2>
      <p className="text-sm text-[#2A2823]/70 mt-2 max-w-2xl leading-relaxed">
        Paste this snippet on a community page, church newsletter, or neighborhood site.
        It shows a small strip of Trusted providers from Willoah, always with the
        "not medical advice — provider recommendations only" line and a link back
        to willoah.com.
      </p>
      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        <label className="text-sm">
          <span className="accent-label text-[#B79256]">area (optional)</span>
          <input
            type="text"
            data-testid="embed-area-input"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="Nashville, TN"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
          />
        </label>
        <label className="text-sm">
          <span className="accent-label text-[#B79256]">provider type (optional)</span>
          <input
            type="text"
            data-testid="embed-type-input"
            value={providerType}
            onChange={(e) => setProviderType(e.target.value)}
            placeholder="Dentist"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
          />
        </label>
        <label className="text-sm">
          <span className="accent-label text-[#B79256]">how many</span>
          <select
            data-testid="embed-limit-input"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2 text-sm"
          >
            {[3, 4, 5, 6, 8, 10, 12].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="accent-label text-[#B79256]">preview</span>
          <Button
            data-testid="embed-copy-snippet"
            onClick={() => copy(snippet, "Embed snippet")}
            className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 shadow-none text-xs"
          >
            Copy embed snippet
          </Button>
        </div>
        <div className="mt-2 rounded-2xl border border-[#B79256]/30 overflow-hidden bg-[#F4EFE3]">
          <iframe
            src={src}
            title="Trusted preview"
            data-testid="embed-preview-iframe"
            className="w-full block"
            style={{ height: 520, border: 0 }}
          />
        </div>
        <pre
          data-testid="embed-snippet-code"
          className="mt-3 whitespace-pre-wrap break-all text-xs bg-white/60 border border-[#B79256]/25 rounded-xl p-3 text-[#2A2823]/80"
        >
          {snippet}
        </pre>
      </div>

      <div className="mt-6" data-testid="embed-landing-snippet">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="accent-label text-[#B79256]">intro paragraph (for the page above the iframe)</span>
          <Button
            data-testid="embed-copy-landing"
            onClick={() => copy(landingText, "Intro paragraph")}
            className="rounded-full bg-[#B79256] text-[#FBF8F1] hover:bg-[#a4823d] h-9 px-4 shadow-none text-xs"
          >
            Copy intro paragraph
          </Button>
        </div>
        <p
          data-testid="embed-landing-text"
          className="mt-2 rounded-2xl bg-white/60 border border-[#B79256]/25 p-4 text-sm text-[#2A2823]/85 leading-relaxed"
        >
          {landingText}
        </p>
      </div>

      <div className="mt-8" data-testid="embed-analytics">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3 className="font-serif-display text-2xl text-[#2A2823]">Where the widget is showing up</h3>
          <button
            type="button"
            data-testid="embed-analytics-refresh"
            onClick={fetchAnalytics}
            className="accent-label text-xs text-[#4E5A3D] hover:text-[#3E4830]"
          >
            Refresh
          </button>
        </div>
        {!analytics ? (
          <p className="text-sm text-[#2A2823]/55 mt-3">Loading…</p>
        ) : analytics.rows.length === 0 ? (
          <p data-testid="embed-analytics-empty" className="text-sm text-[#2A2823]/55 mt-3">
            No embed loads recorded yet. Once someone drops the snippet on a page, hits will show up here.
          </p>
        ) : (
          <>
            <p className="text-sm text-[#2A2823]/70 mt-2">
              <b data-testid="embed-analytics-total">{analytics.total_hits}</b> total loads
              across <b data-testid="embed-analytics-unique">{analytics.unique_referrers}</b> unique pages.
            </p>
            <div className="mt-3 overflow-x-auto rounded-2xl border border-[#B79256]/25 bg-white/60">
              <table className="w-full text-sm">
                <thead className="text-left accent-label text-[#B79256]">
                  <tr>
                    <th className="px-4 py-2">page</th>
                    <th className="px-4 py-2">area</th>
                    <th className="px-4 py-2">type</th>
                    <th className="px-4 py-2 text-right">loads</th>
                    <th className="px-4 py-2">30-day trend</th>
                    <th className="px-4 py-2">last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.rows.map((row, i) => (
                    <tr key={i} data-testid={`embed-analytics-row-${i}`} className="border-t border-[#B79256]/15">
                      <td className="px-4 py-2 text-[#2A2823]/85">
                        {row.referer ? (
                          <a
                            href={row.referer}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#4E5A3D] hover:underline break-all"
                          >
                            {row.referer}
                          </a>
                        ) : (
                          <span className="text-[#2A2823]/40 italic">direct / no referrer</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-[#2A2823]/70">{row.area || "—"}</td>
                      <td className="px-4 py-2 text-[#2A2823]/70">{row.provider_type || "—"}</td>
                      <td className="px-4 py-2 text-right font-semibold text-[#2A2823]">{row.count}</td>
                      <td className="px-4 py-2">
                        <Sparkline
                          data-testid={`embed-analytics-sparkline-${i}`}
                          values={row.sparkline || []}
                          days={row.sparkline_days || []}
                        />
                      </td>
                      <td className="px-4 py-2 text-[#2A2823]/50">
                        {row.last_seen ? new Date(row.last_seen).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}



function Sparkline({ values, days, ...rest }) {
  if (!values || values.length === 0) {
    return <span className="text-[#2A2823]/30 text-xs">—</span>;
  }
  const width = 96;
  const height = 24;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const pts = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - (v / max) * height).toFixed(1)}`)
    .join(" ");
  const total = values.reduce((a, b) => a + b, 0);
  const last7 = values.slice(-7).reduce((a, b) => a + b, 0);
  const prev7 = values.slice(-14, -7).reduce((a, b) => a + b, 0);
  const trending = last7 > prev7 ? "up" : last7 < prev7 ? "down" : "flat";
  const trendColor =
    trending === "up" ? "#4E5A3D" : trending === "down" ? "#B24A2A" : "#B79256";
  return (
    <div className="flex items-center gap-2" {...rest}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`30-day trend: ${total} loads, trending ${trending}`}
        className="overflow-visible"
      >
        <polyline
          fill="none"
          stroke={trendColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={pts}
        />
        {values.length > 0 && (
          <circle
            cx={((values.length - 1) * step).toFixed(1)}
            cy={(height - (values[values.length - 1] / max) * height).toFixed(1)}
            r={2}
            fill={trendColor}
          />
        )}
      </svg>
      <span className="text-[10px] text-[#2A2823]/50">
        7d: {last7} / prev: {prev7}
      </span>
    </div>
  );
}
